import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export type InvoiceItem = {
  name: string;
  qty: number;
  price: number;
  color?: string | null;
  size?: string | null;
  image?: string | null;
};

export type InvoiceData = {
  invoiceNo: string;
  date: string;
  orderId?: string;
  paymentMethod?: string | null;
  paymentStatus?: string | null; // "received" | "pending" | "paid" | etc.
  orderStatus?: string | null;
  customer: { name: string; phone?: string; email?: string; address?: string };
  items: InvoiceItem[];
  discount?: number;
  tax?: number;
  shipping?: number;
  notes?: string;
};

// Brand
const BRAND = {
  name: "Rupshoili",
  tagline: "Premium Fashion Store",
  city: "Dhaka, Bangladesh",
  email: "support@rupshoili.com",
  website: "www.rupshoili.com",
  supportPhone: "+8801711779774",
  supportEmail: "shoilirup@gmail.com",
  logoUrl: "/logo.png",
};

const ACCENT: [number, number, number] = [249, 115, 22]; // #F97316
const ACCENT_SOFT: [number, number, number] = [255, 237, 213]; // orange-100
const INK: [number, number, number] = [17, 24, 39]; // gray-900
const MUTED: [number, number, number] = [107, 114, 128]; // gray-500
const LINE: [number, number, number] = [229, 231, 235]; // gray-200
const CARD_BG: [number, number, number] = [250, 250, 250];

const fmt = (n: number) =>
  `BDT ${n.toLocaleString("en-BD", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

type LoadedImage = { dataUrl: string; w: number; h: number; type: "PNG" | "JPEG" };

const loadImageAsDataUrl = async (url: string): Promise<LoadedImage | null> => {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
    const dims = await new Promise<{ w: number; h: number }>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.width, h: img.height });
      img.onerror = () => resolve({ w: 1, h: 1 });
      img.src = dataUrl;
    });
    const type: "PNG" | "JPEG" = dataUrl.includes("image/png") ? "PNG" : "JPEG";
    return { dataUrl, ...dims, type };
  } catch {
    return null;
  }
};

const roundRect = (
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  style: "F" | "S" | "FD",
) => {
  (doc as any).roundedRect(x, y, w, h, r, r, style);
};

const statusBadge = (
  doc: jsPDF,
  paymentStatus: string | undefined | null,
  paymentMethod: string | undefined | null,
  x: number,
  y: number,
) => {
  let label = "PENDING";
  let bg: [number, number, number] = [254, 243, 199]; // amber-100
  let fg: [number, number, number] = [146, 64, 14]; // amber-800

  const ps = (paymentStatus || "").toLowerCase();
  const pm = (paymentMethod || "").toLowerCase();
  if (ps === "received" || ps === "paid") {
    label = "PAID";
    bg = [220, 252, 231]; // green-100
    fg = [22, 101, 52]; // green-800
  } else if (pm === "cod" || pm === "cash on delivery") {
    label = "COD";
    bg = [219, 234, 254]; // blue-100
    fg = [30, 64, 175]; // blue-800
  }

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  const tw = doc.getTextWidth(label) + 8;
  const th = 6.5;
  doc.setFillColor(...bg);
  roundRect(doc, x - tw, y - th + 1, tw, th, 1.6, "F");
  doc.setTextColor(...fg);
  doc.text(label, x - tw / 2, y - 1.2, { align: "center" });
  return tw;
};

export const buildInvoicePdf = async (data: InvoiceData) => {
  const {
    invoiceNo,
    date,
    orderId,
    paymentMethod,
    paymentStatus,
    orderStatus,
    customer,
    items,
    discount = 0,
    tax = 0,
    shipping = 0,
    notes,
  } = data;

  const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
  const grandTotal = Math.max(0, subtotal - discount + tax + shipping);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 14;
  const contentW = pageW - margin * 2;

  // ─── Top accent bar ────────────────────────────────────────────────
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, pageW, 4, "F");

  // ─── Header: logo + brand (left) ───────────────────────────────────
  const logo = await loadImageAsDataUrl(BRAND.logoUrl);
  const headerTop = 12;
  const logoSize = 18;
  let textX = margin;
  if (logo) {
    try {
      doc.addImage(logo.dataUrl, logo.type, margin, headerTop, logoSize, logoSize);
      textX = margin + logoSize + 5;
    } catch {
      /* ignore */
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...ACCENT);
  doc.text(BRAND.name, textX, headerTop + 7);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(BRAND.tagline, textX, headerTop + 11.5);
  doc.text(BRAND.city, textX, headerTop + 15);
  doc.text(`${BRAND.email}  •  ${BRAND.website}`, textX, headerTop + 18.5);

  // ─── Header: INVOICE title + meta (right) ──────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  doc.text("INVOICE", pageW - margin, headerTop + 6, { align: "right" });

  // Status badge under title
  statusBadge(doc, paymentStatus, paymentMethod, pageW - margin, headerTop + 13);

  // ─── Divider ───────────────────────────────────────────────────────
  const dividerY = headerTop + 24;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(margin, dividerY, pageW - margin, dividerY);

  // ─── Meta block (two-column under divider) ─────────────────────────
  const metaTop = dividerY + 8;
  const metaRows: { label: string; value: string }[] = [
    { label: "Invoice No", value: invoiceNo },
    { label: "Order ID", value: orderId ? `#${orderId.slice(0, 8).toUpperCase()}` : "—" },
    { label: "Order Date", value: date },
    { label: "Payment Method", value: paymentMethod ? paymentMethod.toUpperCase() : "—" },
    { label: "Order Status", value: orderStatus ? orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1) : "—" },
  ];

  // Customer card (left) + Order meta card (right)
  const cardGap = 6;
  const cardW = (contentW - cardGap) / 2;
  const cardH = 46;
  const cardY = metaTop;

  // Left card — Bill To
  doc.setFillColor(...CARD_BG);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  roundRect(doc, margin, cardY, cardW, cardH, 2.5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...ACCENT);
  doc.text("BILL TO", margin + 5, cardY + 6);

  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text(customer.name || "—", margin + 5, cardY + 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  const billLines = [
    customer.phone && `Phone: ${customer.phone}`,
    customer.email && customer.email,
    customer.address,
  ].filter(Boolean) as string[];
  let by = cardY + 19;
  billLines.forEach((l) => {
    const wrapped = doc.splitTextToSize(l, cardW - 10);
    wrapped.forEach((line: string) => {
      if (by < cardY + cardH - 3) {
        doc.text(line, margin + 5, by);
        by += 4.2;
      }
    });
  });

  // Right card — Order meta
  const rx = margin + cardW + cardGap;
  doc.setFillColor(...CARD_BG);
  roundRect(doc, rx, cardY, cardW, cardH, 2.5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...ACCENT);
  doc.text("ORDER DETAILS", rx + 5, cardY + 6);

  doc.setFontSize(8.5);
  let my = cardY + 12;
  metaRows.forEach((r) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(r.label, rx + 5, my);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(r.value, rx + cardW - 5, my, { align: "right" });
    my += 6.5;
  });

  // ─── Products table ────────────────────────────────────────────────
  const tableStartY = cardY + cardH + 8;

  const imageCache = await Promise.all(
    items.map((it) => (it.image ? loadImageAsDataUrl(it.image) : Promise.resolve(null))),
  );

  const rowH = 22; // bigger for visible product image
  autoTable(doc, {
    startY: tableStartY,
    head: [["#", "Image", "Product", "Qty", "Unit Price", "Total"]],
    body: items.map((it, i) => {
      const variant = [
        it.color ? `Color: ${it.color}` : null,
        it.size ? `Size: ${it.size}` : null,
      ]
        .filter(Boolean)
        .join("   ");
      return [
        String(i + 1),
        "",
        variant ? `${it.name || "—"}\n${variant}` : it.name || "—",
        String(it.qty),
        fmt(it.price),
        fmt(it.qty * it.price),
      ];
    }),
    theme: "plain",
    margin: { left: margin, right: margin },
    headStyles: {
      fillColor: ACCENT,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
    },
    bodyStyles: {
      fontSize: 9.5,
      textColor: INK,
      minCellHeight: rowH,
      valign: "middle",
      cellPadding: { top: 3, bottom: 3, left: 3, right: 3 },
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    columnStyles: {
      0: { cellWidth: 8, halign: "center", textColor: MUTED },
      1: { cellWidth: 24, halign: "center" },
      2: { cellWidth: "auto" as any },
      3: { cellWidth: 12, halign: "center" },
      4: { cellWidth: 30, halign: "right" },
      5: { cellWidth: 30, halign: "right", fontStyle: "bold" },
    },
    styles: { lineColor: LINE, lineWidth: 0.15 },
    didParseCell: (hookData) => {
      if (hookData.section === "body") {
        hookData.cell.styles.lineWidth = 0;
      }
    },
    didDrawCell: (hookData) => {
      // Bottom border for each body row
      if (hookData.section === "body" && hookData.column.index === 0) {
        const y = hookData.cell.y + hookData.cell.height;
        doc.setDrawColor(...LINE);
        doc.setLineWidth(0.15);
        doc.line(margin, y, pageW - margin, y);
      }
      // Image cell
      if (hookData.section === "body" && hookData.column.index === 1) {
        const img = imageCache[hookData.row.index];
        if (!img) return;
        const pad = 2;
        const size = Math.min(hookData.cell.width, hookData.cell.height) - pad * 2;
        const x = hookData.cell.x + (hookData.cell.width - size) / 2;
        const y = hookData.cell.y + (hookData.cell.height - size) / 2;
        try {
          // Subtle frame
          doc.setFillColor(245, 245, 245);
          roundRect(doc, x, y, size, size, 1.2, "F");
          doc.addImage(img.dataUrl, img.type, x, y, size, size);
        } catch {
          /* ignore */
        }
      }
    },
  });

  const afterTableY = (doc as any).lastAutoTable.finalY + 8;

  // ─── Summary card (right-aligned) ──────────────────────────────────
  const sumW = 80;
  const sumX = pageW - margin - sumW;
  let sumY = afterTableY;
  const lineH = 6;

  const rows: [string, string][] = [["Subtotal", fmt(subtotal)]];
  if (shipping) rows.push(["Shipping", fmt(shipping)]);
  if (discount) rows.push(["Discount", `- ${fmt(discount)}`]);
  if (tax) rows.push(["Tax", fmt(tax)]);

  const summaryH = rows.length * lineH + 18; // + grand total band
  doc.setFillColor(...CARD_BG);
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  roundRect(doc, sumX, sumY, sumW, summaryH, 2.5, "FD");

  doc.setFontSize(9.5);
  let ry = sumY + 7;
  rows.forEach(([label, value]) => {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...MUTED);
    doc.text(label, sumX + 5, ry);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...INK);
    doc.text(value, sumX + sumW - 5, ry, { align: "right" });
    ry += lineH;
  });

  // Grand Total band
  const gtBandY = sumY + summaryH - 12;
  doc.setFillColor(...ACCENT);
  roundRect(doc, sumX, gtBandY, sumW, 12, 2.5, "F");
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("GRAND TOTAL", sumX + 5, gtBandY + 7.5);
  doc.setFontSize(12.5);
  doc.text(fmt(grandTotal), sumX + sumW - 5, gtBandY + 7.5, { align: "right" });

  // Optional notes (left side, opposite summary)
  if (notes) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8.5);
    doc.setTextColor(...MUTED);
    const wrapped = doc.splitTextToSize(notes, contentW - sumW - 6);
    doc.text(wrapped, margin, afterTableY + 5);
  }

  // ─── Footer ────────────────────────────────────────────────────────
  const footerTop = pageH - 32;
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.2);
  doc.line(margin, footerTop, pageW - margin, footerTop);

  // Thank you
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...ACCENT);
  doc.text("Thank you for shopping with Rupshoili", margin, footerTop + 6);

  // Heart accent dot
  doc.setFillColor(...ACCENT);
  doc.circle(margin + doc.getTextWidth("Thank you for shopping with Rupshoili") + 2.5, footerTop + 5, 1.2, "F");

  // Support line
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...MUTED);
  doc.text(
    `For support — Phone: ${BRAND.supportPhone}   •   Email: ${BRAND.supportEmail}   •   ${BRAND.website}`,
    margin,
    footerTop + 12,
  );

  // Page number / computer-generated note
  doc.setFontSize(7.5);
  doc.setTextColor(160);
  doc.text("This is a computer-generated invoice and does not require a signature.", margin, footerTop + 18);
  doc.text(`Generated on ${new Date().toLocaleString("en-GB")}`, pageW - margin, footerTop + 18, { align: "right" });

  // Bottom accent bar
  doc.setFillColor(...ACCENT);
  doc.rect(0, pageH - 3, pageW, 3, "F");

  return doc;
};

export const invoiceNoFromOrder = (order: { id: string; created_at: string }) => {
  const d = new Date(order.created_at);
  const y = d.getFullYear().toString().slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `RUP-${y}${m}${day}-${order.id.slice(0, 6).toUpperCase()}`;
};
